/** Twilio <Say> voice / language options for ECX call templates. */
export const CALL_CONTENT_MAX_LENGTH = 2800;
export const CALL_PROMPT_MAX_LENGTH = 1000;

export const CALL_VOICE_OPTIONS = [
    { value: 'Polly.Joanna', label: 'Polly Joanna (US English, Standard)' },
    { value: 'Polly.Matthew', label: 'Polly Matthew (US English, Standard)' },
    { value: 'Polly.Amy', label: 'Polly Amy (UK English, Standard)' },
    { value: 'Polly.Brian', label: 'Polly Brian (UK English, Standard)' },
    { value: 'Polly.Joanna-Neural', label: 'Polly Joanna Neural (US English)' },
    { value: 'Polly.Matthew-Neural', label: 'Polly Matthew Neural (US English)' },
    { value: 'woman', label: 'Basic Woman (free TTS)' },
    { value: 'man', label: 'Basic Man (free TTS)' },
];

export const CALL_LANGUAGE_OPTIONS = [
    { value: 'en-US', label: 'English (United States)' },
    { value: 'en-GB', label: 'English (United Kingdom)' },
    { value: 'en-CA', label: 'English (Canada)' },
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'fr-CA', label: 'French (Canada)' },
    { value: 'fr-FR', label: 'French (France)' },
    { value: 'es-US', label: 'Spanish (United States)' },
    { value: 'es-ES', label: 'Spanish (Spain)' },
];

export const DEFAULT_CALL_VOICE = 'Polly.Joanna';
export const DEFAULT_CALL_LANGUAGE = 'en-US';

export const CALL_INTERACTION_TYPES = [
    {
        value: 'NONE',
        label: 'Announce only',
        help: 'Speak the script and hang up — no patient input',
    },
    {
        value: 'DTMF',
        label: 'DTMF options (press 1, 2, 3…)',
        help: 'Gather a single digit and run the mapped action',
    },
];

export const DEFAULT_CALL_INTERACTION_TYPE = 'NONE';

export const CALL_OPTION_ACTIONS = [
    { value: 'CONFIRM', label: 'Confirm' },
    { value: 'CANCEL', label: 'Cancel' },
    { value: 'RESCHEDULE', label: 'Reschedule' },
    { value: 'REPEAT', label: 'Repeat script' },
    { value: 'TRANSFER', label: 'Transfer to clinic' },
    { value: 'ACKNOWLEDGE', label: 'Acknowledge' },
];

export const CALL_DIGIT_CHOICES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

export const DEFAULT_DTMF_OPTION_ROWS = [
    { digit: '1', action: 'CONFIRM', say: 'Thank you. Your appointment is confirmed.' },
    { digit: '2', action: 'CANCEL', say: 'Okay. We will cancel this appointment.' },
    { digit: '3', action: 'REPEAT', say: '' },
];

/**
 * Convert API options JSON map → editable row list.
 * @param {Record<string, {action?: string, say?: string}>|null|undefined} options
 */
export function optionsMapToRows(options) {
    if (!options || typeof options !== 'object') {
        return [];
    }
    return Object.keys(options)
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
        .map((digit) => {
            const entry = options[digit] || {};
            return {
                digit: String(digit),
                action: entry.action || 'CONFIRM',
                say: entry.say != null ? String(entry.say) : '',
            };
        });
}

/**
 * Convert editable rows → API options JSON map (null if empty).
 * @param {Array<{digit: string, action: string, say: string}>} rows
 */
export function optionRowsToMap(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    const map = {};
    rows.forEach((row) => {
        const digit = String(row.digit || '').trim();
        if (!digit) return;
        map[digit] = {
            action: row.action || 'CONFIRM',
            say: row.say?.trim() ? row.say.trim() : null,
        };
    });
    return Object.keys(map).length ? map : null;
}

/** Stable stringify for dirty-checking options maps. */
export function serializeCallOptions(options) {
    if (!options || typeof options !== 'object') {
        return '';
    }
    const keys = Object.keys(options).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
    );
    const normalized = {};
    keys.forEach((key) => {
        const entry = options[key] || {};
        normalized[key] = {
            action: entry.action || null,
            say: entry.say != null && String(entry.say).trim() ? String(entry.say).trim() : null,
        };
    });
    return JSON.stringify(normalized);
}
